import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import type { TransferPanelManageData } from "src/components/Form/panels/TransferPanel/TransferPanel.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";

type Variables = {
  data: TransferPanelManageData;
};
export const useManageTransferListRecords = ({
  resource,
  formResource,
  params,
  mutationOptions,
}: {
  resource: string;
  formResource: string;
  params: {
    id: string;
  };
  mutationOptions: Omit<UseMutationOptions<any, any, Variables>, "mutationFn">;
}) => {
  return useMutation({
    mutationFn: async ({ data }) => {
      const manageData = getManageData({
        resource,
        formResource,
        params,
      });
      return await manageData(data);
    },
    ...mutationOptions,
  });
};

const getManageData = ({
  resource: transferResource,
  formResource: mainResource,
  params,
}: {
  resource: string;
  formResource: string;
  params: {
    id: string;
  };
}): ManageDataFn => {
  if (transferResource === ResourceEnum.DeliveryService) {
    return async ({ rows, locale, ...restData }) => {
      const api = getExonnApi();
      return (
        await api.deliveryMethod.manageDeliveryServices(
          params,
          {
            ids: rows?.map((row) => row.id),
            ...restData,
          },
          {
            headers: {
              "X-Locale": locale,
            },
          },
        )
      ).data;
    };
  }
  if (
    mainResource === ResourceEnum.Manufacturer &&
    transferResource === ResourceEnum.Variant
  ) {
    return async ({ rows, locale, ...restData }: TransferPanelManageData) => {
      const api = getExonnApi();
      return (
        await api.manufacturer.manageProducts(
          params,
          {
            ids: rows?.map((row) => row.id),
            ...restData,
          },
          {
            headers: {
              "X-Locale": locale,
            },
          },
        )
      ).data;
    };
  }

  if (transferResource === ResourceEnum.Variant) {
    return async ({ rows, locale, ...restData }: TransferPanelManageData) => {
      const api = getExonnApi();
      return (
        await api.system.productTransferList.manageRows(
          {},
          {
            ...params,
            ids: rows?.map((row) => row.id),
            types: rows?.map((row) => row.item_type),
            type: mainResource,
            ...restData,
          },
          {
            headers: {
              "X-Locale": locale,
            },
          },
        )
      ).data;
    };
  }

  return async ({ rows, locale, ...restData }: TransferPanelManageData) => {
    const api = getExonnApi();
    return (
      await api.system.transferList.manageRows(
        {},
        {
          ...restData,
          ...params,
          ids: rows?.map((row) => row.id),
          type: mainResource,
          transfer_type: transferResource,
        },
        {
          headers: {
            "X-Locale": locale,
          },
        },
      )
    ).data;
  };
};

type ManageDataFn = (data: TransferPanelManageData) => Promise<any>;
